import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as React from 'react';
import { createContext } from 'react';
import { isMobile } from 'react-device-detect';

import { InfoIcon } from 'lucide-react';
import { cn } from '../../lib/utils.ts';

type TooltipTriggerContextType = {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const TooltipTriggerContext = createContext<TooltipTriggerContextType>({
    open: false,
    setOpen: () => {
        // noop
    },
});

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip: React.FC<TooltipPrimitive.TooltipProps> = ({ children, ...props }) => {
    const [open, setOpen] = React.useState<boolean>(props.defaultOpen ?? false);

    // we only want to enable the "click to open" functionality on mobile

    return (
        <TooltipPrimitive.Root
            delayDuration={props.delayDuration}
            onOpenChange={(e) => {
                setOpen(e);
                props.onOpenChange?.(e);
            }}
            open={open}
        >
            <TooltipTriggerContext.Provider value={{ open, setOpen }}>
                {children}
            </TooltipTriggerContext.Provider>
        </TooltipPrimitive.Root>
    );
};
Tooltip.displayName = TooltipPrimitive.Root.displayName;

const TooltipTrigger = React.forwardRef<
    React.ElementRef<typeof TooltipPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ children, ...props }, ref) => {
    const { setOpen } = React.useContext(TooltipTriggerContext);

    return (
        <TooltipPrimitive.Trigger
            ref={ref}
            {...props}
            onClick={(e) => {
                if (!isMobile) {
                    e.preventDefault();
                }
                setOpen(true);
            }}
        >
            {children}
        </TooltipPrimitive.Trigger>
    );
});
TooltipTrigger.displayName = TooltipPrimitive.TooltipTrigger.displayName;

const TooltipContent = React.forwardRef<
    React.ElementRef<typeof TooltipPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, children, ...props }, ref) => (
    <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
            ref={ref}
            sideOffset={sideOffset}
            className={cn('z-50 rounded-lg bg-neutral-800 px-3 py-2 text-white shadow-md text-sm', className)}
            {...props}
        >
            {children}
            <TooltipPrimitive.Arrow className="fill-neutral-800" width={12} height={6} />
        </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export type TooltipSide = 'top' | 'right' | 'bottom' | 'left';

type TooltipInfoProps = {
    side?: TooltipSide;
    onOpenChange?: (open: boolean) => void;
} & React.PropsWithChildren;

const TooltipInfo = ({ children, side, onOpenChange }: TooltipInfoProps): React.ReactElement => {
    return (
        <Tooltip onOpenChange={onOpenChange}>
            <TooltipTrigger>
                <InfoIcon className="h-4 w-4 text-neutral-400" />
            </TooltipTrigger>
            <TooltipContent side={side}>{children}</TooltipContent>
        </Tooltip>
    );
};

export { Tooltip, TooltipContent, TooltipInfo, TooltipProvider, TooltipTrigger };
